import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import {
  ArrowRight,
  CircleCheck,
  Clock3,
  Download,
  FileSearch,
  FolderKanban,
  Map,
  Satellite,
} from "lucide-react"

import { MetricStrip } from "@/components/metric-strip"
import { ScenePreview } from "@/components/scene-preview"
import { OrderStatusBadge } from "@/components/status-badge"
import { WorkspacePageHeader } from "@/components/workspace-page-header"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { dashboardQueryOptions } from "@/lib/query-options"

export const Route = createFileRoute("/portal/")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(dashboardQueryOptions()),
  head: () => ({
    meta: [{ title: "Dashboard · Service Portal" }],
  }),
  component: AnalystDashboard,
})

function AnalystDashboard() {
  const { data } = useSuspenseQuery(dashboardQueryOptions())
  const { viewer, statistics, orders, activities, recommendedScenes } = data

  return (
    <div className="mx-auto max-w-[1400px]">
      <WorkspacePageHeader
        kicker={`Good afternoon, ${viewer.shortName}`}
        title="Imagery request dashboard"
        description="Track archive and new-tasking requests, watch your current download allocation, and return to recent catalogue matches."
        actions={
          <>
            <Button render={<Link to="/portal/orders" />} variant="outline">
              <FolderKanban />
              View all orders
            </Button>
            <Button render={<Link to="/portal/explorer" />}>
              <Map />
              Search imagery
            </Button>
          </>
        }
      />

      <MetricStrip
        className="mt-7"
        items={[
          {
            label: "Requests in motion",
            value: statistics.newOrders,
            detail: "Submitted or under review",
            icon: Clock3,
            tone: "yellow",
          },
          {
            label: "Approved / ready",
            value: statistics.approvedOrders,
            detail: "Cleared by demo operations",
            icon: CircleCheck,
            tone: "green",
          },
          {
            label: "Available downloads",
            value: statistics.availableDownloads,
            detail: `${statistics.remainingFiles} of 8 file slots remain`,
            icon: Download,
            tone: "cyan",
          },
          {
            label: "Size allocation left",
            value: `${Math.max(0, statistics.remainingSizeGb).toFixed(1)} GB`,
            detail: "Synthetic 25.0 GB allowance",
            icon: FileSearch,
            tone: "navy",
          },
        ]}
      />

      <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <section className="min-w-0 overflow-hidden rounded-[14px] border border-border bg-white">
          <div className="flex items-center justify-between gap-4 border-b border-border px-5 py-4">
            <div>
              <h2 className="font-semibold text-[var(--navy)]">
                Recent requests
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Archive and new satellite tasking in one queue
              </p>
            </div>
            <Button
              render={<Link to="/portal/orders" />}
              variant="ghost"
              size="sm"
            >
              Full history
              <ArrowRight />
            </Button>
          </div>

          <div className="hidden overflow-x-auto md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Request</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.slice(0, 4).map((order) => (
                  <TableRow key={order.id}>
                    <TableCell className="max-w-[340px]">
                      <p className="truncate font-semibold text-[var(--navy)]">
                        {order.title}
                      </p>
                      <p className="mt-1 font-mono text-[0.65rem] text-muted-foreground">
                        {order.id}
                      </p>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs font-medium">{order.kind}</span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {order.submittedLabel}
                    </TableCell>
                    <TableCell>
                      <OrderStatusBadge status={order.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="divide-y divide-border md:hidden">
            {orders.slice(0, 4).map((order) => (
              <article key={order.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-[var(--navy)]">
                      {order.title}
                    </p>
                    <p className="mt-1 font-mono text-[0.65rem] text-muted-foreground">
                      {order.id}
                    </p>
                  </div>
                  <OrderStatusBadge status={order.status} />
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                  {order.kind} · {order.submittedLabel}
                </p>
              </article>
            ))}
          </div>
        </section>

        <aside className="overflow-hidden rounded-[14px] border border-border bg-white">
          <div className="border-b border-border px-5 py-4">
            <h2 className="font-semibold text-[var(--navy)]">
              Recent activity
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Latest changes across this demo account
            </p>
          </div>
          <div className="divide-y divide-border">
            {activities.slice(0, 4).map((activity) => {
              const ActivityIcon =
                activity.kind === "download"
                  ? Download
                  : activity.kind === "order"
                    ? FolderKanban
                    : activity.kind === "catalogue"
                      ? Satellite
                      : CircleCheck
              return (
                <div key={activity.id} className="flex gap-3 px-5 py-4">
                  <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-[9px] bg-[#e8f4f7] text-[var(--service-blue)]">
                    <ActivityIcon className="size-3.5" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-[var(--ink)]">
                      {activity.title}
                    </p>
                    <p className="mt-1 line-clamp-2 text-[0.68rem] leading-5 text-muted-foreground">
                      {activity.detail}
                    </p>
                    <p className="mt-1.5 text-[0.62rem] text-[var(--telemetry)]">
                      {activity.timeLabel}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </aside>
      </div>

      <section className="mt-6 rounded-[14px] border border-border bg-white p-5 sm:p-6">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
          <div>
            <p className="text-xs font-semibold text-[var(--service-blue)]">
              Catalogue watch
            </p>
            <h2 className="mt-1 text-xl font-semibold tracking-[-0.02em] text-[var(--navy)]">
              Scenes near your recent areas
            </h2>
          </div>
          <p className="text-xs text-muted-foreground">
            Synthetic recommendations · 27 Jul 2026
          </p>
        </div>
        <div className="mt-5 grid gap-4 lg:grid-cols-3">
          {recommendedScenes.map((scene) => (
            <article
              key={scene.id}
              className="grid overflow-hidden rounded-[12px] border border-border sm:grid-cols-[156px_1fr] lg:grid-cols-1"
            >
              <ScenePreview
                scene={scene}
                className="min-h-[136px] rounded-none lg:aspect-[16/8] lg:min-h-0"
              />
              <div className="min-w-0 p-4">
                <p className="text-xs font-semibold text-[var(--service-blue)]">
                  {scene.satellite} · {scene.type}
                </p>
                <h3 className="mt-1 truncate font-semibold text-[var(--navy)]">
                  {scene.location}
                </h3>
                <p className="mt-2 text-xs leading-5 text-muted-foreground">
                  {scene.capturedLabel} · {scene.resolutionMeters} m
                </p>
                <Button
                  render={
                    <Link to="/portal/explorer" search={{ scene: scene.id }} />
                  }
                  variant="ghost"
                  size="sm"
                  className="mt-3 -ml-2"
                >
                  Inspect scene
                  <ArrowRight />
                </Button>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  )
}
